import React, { useEffect, useState } from 'react';
import { Box, CircularProgress } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { Autocomplete } from '@material-ui/lab';
import { Add } from '@material-ui/icons';
import { useDeliveryListStockCardsLazyQuery } from '../../../../../../../generated/graphql';
import { useWarehousesScope } from '../../../../../../warehouse/providers/WarehouseScopeProvider';
import { useDebounce } from '../../../../../../../utils';
import { DeliveryListFormValues, StockCard, StockCards } from '../../../types';
import { TextField, Typography } from '../../../../../../../ui-kit';
import { CREATE_NEW_STRING, FETCH_MORE_STRING } from '../../constants';

const LIMIT = 10;

interface StockCardSearchProps {
    openMobilePopup?(): void;
    onAdd(stockCard: StockCard): void;
    pickedStockCards: DeliveryListFormValues['deliveryListItems'];
    onCreateNewClick(): void;
    passedSearchValue?: string;
    isSingleSelect?: boolean;
}

interface OptionProps {
    option: StockCard;
    isLoading?: boolean;
}


const Option = ({ option, isLoading }: OptionProps) => {
    const { t } = useTranslation();
    const { id, name, conversionUnit, convertedAmount, storageUnit } = option;

    if(id === CREATE_NEW_STRING){
        return (
            <Box display='flex'>
                <Add color='primary' />
                <Box mr={0.5} />
                <Typography color='primary'>{t('New warehouse card')}</Typography>
            </Box>
        );
    }
    if(id === FETCH_MORE_STRING){
        return isLoading
            ? <CircularProgress size={16} />
            : <Typography>{t('Show more')}</Typography>;
    }
    let postfix = storageUnit.toString();
    if(conversionUnit && convertedAmount){
        postfix = `${convertedAmount} ${conversionUnit}`;
    }
    return (
        <Box display='grid' gridGap='0px 4px' gridAutoFlow='column'>
            <Typography>{name}</Typography>
            <Typography color='textSecondary'>{postfix}</Typography>
        </Box>
    );
};

const StockCardSearch = ({
    openMobilePopup,
    onAdd,
    pickedStockCards,
    onCreateNewClick,
    passedSearchValue,
    isSingleSelect,
}: StockCardSearchProps) => {
    const [offset, setOffset] = useState(0);
    const [searchQuery, setSearchQuery] = useState(passedSearchValue || '');
    const [hasMore, setHasMore] = useState(true);
    const [isFetchingMore, setIsFetchingMore] = useState(false);
    const { currentWarehouseId } = useWarehousesScope();
    const [getData, { data, fetchMore }] = useDeliveryListStockCardsLazyQuery({ nextFetchPolicy: 'cache-first' });
    const { t } = useTranslation();

    const debouncedSearchQuery = useDebounce(searchQuery);
    let options: StockCards = [{ id: CREATE_NEW_STRING } as StockCard, ...(data ? data.stockCards : [])];

    useEffect(() => {
        if(data){
            if(data?.stockCards.length === 0 || data.stockCards.length % LIMIT !== 0){
                setHasMore(false);
            } else {
                setHasMore(true);
            }
        }
    }, [data]);

    useEffect(() => {
        if(passedSearchValue){
            setSearchQuery(passedSearchValue);
        }
    }, [passedSearchValue]);

    useEffect(() => {
        setOffset(0);
        getData({ variables: { nameOrSku: debouncedSearchQuery, warehouseIds: [currentWarehouseId as string], offset: 0, limit: LIMIT } });
    }, [debouncedSearchQuery, getData, currentWarehouseId]);

    const handleFetchMore = async () => {
        if(fetchMore){
            const newOffset = offset + LIMIT;
            setIsFetchingMore(true);
            const { data: newData } = await fetchMore({ variables: { offset: newOffset } });
            setIsFetchingMore(false);
            setOffset(newOffset);
            options = [{ id: CREATE_NEW_STRING } as StockCard, ...newData.stockCards];
            if(newData.stockCards.length < LIMIT){
                setHasMore(false);
            }
        }

    };

    const handleInputChange = (_event: unknown, value: string) => {
        setSearchQuery(value);
    };

    const handleItemClick = async (_event: unknown, value: (StockCard | string)[]) => {
        const lastIndex = value.length - 1;
        const lastItem = value[lastIndex];
        if(lastItem && typeof lastItem !== 'string'){
            if(lastItem.id === FETCH_MORE_STRING){
                await handleFetchMore();
                return;
            }

            if (lastItem.id === CREATE_NEW_STRING) {
                onCreateNewClick();
                return;
            }

            onAdd(lastItem);
        }
    };

    if(hasMore){
        options.push({ id: FETCH_MORE_STRING } as StockCard);
    }
    return (
        <Autocomplete
            getOptionSelected={() => false}
            size='small'
            disableCloseOnSelect={!isSingleSelect}
            renderOption={option => <Option option={option} isLoading={isFetchingMore} />}
            multiple
            disabled={!!openMobilePopup}
            renderTags={() => null}
            getOptionDisabled={o => !!pickedStockCards.find(s => s.stockCardId === o.id)}
            onChange={handleItemClick}
            filterOptions={o => o}
            options={options}
            inputValue={searchQuery}
            onInputChange={handleInputChange}
            renderInput={(
                { InputProps: { className, endAdornment, ...restInputProps }, inputProps, InputLabelProps: _omitted, ...props }
            ) => (
                <TextField
                    {...restInputProps}
                    inputProps={inputProps}
                    label={t('Search ingredient')}
                    margin='none'
                    onClick={openMobilePopup}
                    {...props}
                />
            )}
        />
    );
};

export default StockCardSearch;
